import {
  apiService,
  extractSyncResponse,
  getModelCount,
  ISyncChangesResponse,
  coachingDB,
  updateToken,
  userDB,
  FeatureFlagNames,
} from '..';
import axios from 'axios';
import { tlLogger } from '@taleemabad/shared';
import { SyncRecordStatus } from '../types/sync-state';
import { userCache } from '../utils/user-cache';
import {
  IObservation,
  IObservationAnswer,
  IObservationQuestion,
  IObservationQuestionGroup,
  IObservationSection,
  IObservationTemplate,
  IQuestionOption,
  IVisitPlan,
  ISchoolVisit,
  ITeacherVisit,
  ICoachObservation,
  IObservationFeedback,
  IRegion,
  ISchool,
  ITeacherContext,
  ISchoolClassContext,
  ISchoolClassSubjectContext,
  ILessonPlanMini,
  IBookChapterMini,
} from '../types/coaching';
import { IGrades, ISubject, IGradeSubject } from '../types/lessonplan';

const PAGE_SIZE = 1000;

/**
 * Derives wrongChoiceOptions from studentScores.wrongOptions on server pull.
 * Backend stores wrong options only inside the student_scores JSON blob — this
 * maps them back to the top-level Dexie field so the UI can restore red chips.
 */
function hydrateWrongChoiceOptions(data: Record<string, any>): void {
  const answers = data.observationAnswers;
  if (!answers) return;
  for (const key of ['created', 'updated'] as const) {
    if (!answers[key]?.data) continue;
    answers[key].data = answers[key].data.map((record: any) => ({
      ...(record ?? {}),
      wrongChoiceOptions:
        record?.wrongChoiceOptions ?? record?.studentScores?.wrongOptions ?? [],
    }));
  }
}

// Shared model list for observation template sync functions
const OBSERVATION_TEMPLATE_MODELS = [
  'observationTemplates',
  'observationSections',
  'observationQuestionGroups',
  'observationQuestions',
  'questionOptions',
  'observations',
  'observationAnswers',
] as const;

interface IAnswer {
  questionPrompt: string;
  answer: string;
}

interface IGenerateFeedbackByIdRequest {
  observationId: string;
}

interface IGenerateFeedbackByDataRequest {
  metadata: {
    observationId: string;
    teacherId: number | null;
    lessonPlanId: number | null;
    grade: string | null;
    subject: string | null;
    observationDate: string;
    numberOfBoys: number;
    numberOfGirls: number;
  };
  sections: Array<{
    sectionTitle: string;
    answers: IAnswer[];
  }>;
}

type IGenerateFeedbackRequest =
  | IGenerateFeedbackByIdRequest
  | IGenerateFeedbackByDataRequest;

interface IGenerateFeedbackResponse {
  observationId: string;
  feedback: {
    strengths: string[];
    areasOfGrowth: string[];
  };
  tokensUsed: {
    inputTokensUsed: number;
    outputTokensUsed: number;
    cacheCreationInputTokens: number;
    cacheReadInputTokens: number;
  };
}

interface IObservationTemplateResponse {
  timestamp: string;
  observationTemplates: ISyncChangesResponse<IObservationTemplate>;
  observationSections: ISyncChangesResponse<IObservationSection>;
  observationQuestionGroups: ISyncChangesResponse<IObservationQuestionGroup>;
  observationQuestions: ISyncChangesResponse<IObservationQuestion>;
  questionOptions: ISyncChangesResponse<IQuestionOption>;
  observations: ISyncChangesResponse<IObservation>;
  observationAnswers: ISyncChangesResponse<IObservationAnswer>;
}

interface IObservationContextResponse {
  timestamp: string;
  regions: ISyncChangesResponse<IRegion>;
  schools: ISyncChangesResponse<ISchool>;
  teachers: ISyncChangesResponse<ITeacherContext>;
  schoolClasses: ISyncChangesResponse<ISchoolClassContext>;
  schoolClassSubjects: ISyncChangesResponse<ISchoolClassSubjectContext>;
  lessonPlans: ISyncChangesResponse<ILessonPlanMini>;
  bookChapters: ISyncChangesResponse<IBookChapterMini>;
  grades: ISyncChangesResponse<IGrades>;
  subjects: ISyncChangesResponse<ISubject>;
  gradeSubjects: ISyncChangesResponse<IGradeSubject>;
}

interface IVisitResponse {
  timestamp: string;
  visitPlans: ISyncChangesResponse<IVisitPlan>;
  schoolVisits: ISyncChangesResponse<ISchoolVisit>;
  teacherVisits: ISyncChangesResponse<ITeacherVisit>;
  coachesObservations: ISyncChangesResponse<ICoachObservation>;
}

interface IObservationFeedbackResponse {
  timestamp: string;
  observationFeedbacks: ISyncChangesResponse<IObservationFeedback>;
}

export const fetchObservationTemplates = async (
  timestamp: string | null,
  pageNumber = 1,
): Promise<boolean> => {
  const response = await apiService.get<IObservationTemplateResponse>(
    `/api/v1/sync-observation-templates/?page_size=${PAGE_SIZE}&page=${pageNumber}&c=${Date.now()}&${
      timestamp ? `last_pulled_at=${timestamp}` : ''
    }`,
  );

  const data = response.data;
  let lastResult = true;

  try {
    hydrateWrongChoiceOptions(data);
    await extractSyncResponse(OBSERVATION_TEMPLATE_MODELS, data, coachingDB);
  } catch (e) {
    lastResult = false;
    console.error(`Unable to add data for ${OBSERVATION_TEMPLATE_MODELS}`, e);
  }

  const modelCounts = getModelCount(OBSERVATION_TEMPLATE_MODELS, data);
  const maxPages = Math.max(...modelCounts, 0) / PAGE_SIZE;

  if (pageNumber <= maxPages) {
    lastResult =
      lastResult &&
      (await fetchObservationTemplates(timestamp, pageNumber + 1));
  }

  return lastResult;
};

/**
 * OPTIMIZATION: Fetch the initial batch of observation templates (e.g. first 1-2 pages)
 * This allows observation context to load faster and show UI cards immediately,
 * while ensuring we have the most critical recent data.
 *
 * @param timestamp - Last sync timestamp, or null for full sync
 * @param maxInitialPages - Number of initial pages to fetch (default: 1)
 * @returns Object containing:
 *   - totalPages: Total number of pages available
 *   - nextStartPage: Page number to start fetching remaining pages from
 *   - hasErrors: True if any page failed to extract (for observability/logging only,
 *                the function continues despite errors to maximize data availability)
 */
export const fetchInitialObservationTemplates = async (
  timestamp: string | null,
  maxInitialPages = 1,
): Promise<{
  totalPages: number;
  nextStartPage: number;
  hasErrors: boolean;
}> => {
  let currentPage = 1;
  let maxPages = 1;
  let hasErrors = false;

  // Loop to fetch configured number of initial pages
  while (currentPage <= maxInitialPages) {
    const response = await apiService.get<IObservationTemplateResponse>(
      `/api/v1/sync-observation-templates/?page_size=${PAGE_SIZE}&page=${currentPage}&c=${Date.now()}&${
        timestamp ? `last_pulled_at=${timestamp}` : ''
      }`,
    );

    const data = response.data;

    try {
      hydrateWrongChoiceOptions(data);
      await extractSyncResponse(OBSERVATION_TEMPLATE_MODELS, data, coachingDB);
    } catch (e) {
      hasErrors = true;
      console.error(
        `Unable to add data for ${OBSERVATION_TEMPLATE_MODELS} (page ${currentPage})`,
        e,
      );
    }

    // Calculate total pages needed based on the current response
    const modelCounts = getModelCount(OBSERVATION_TEMPLATE_MODELS, data);
    maxPages = Math.ceil(Math.max(...modelCounts, 0) / PAGE_SIZE);

    // If we've reached the end of data, stop early
    if (currentPage >= maxPages) {
      return {
        totalPages: maxPages,
        nextStartPage: currentPage + 1,
        hasErrors,
      };
    }

    currentPage++;
  }

  return { totalPages: maxPages, nextStartPage: currentPage, hasErrors };
};

/**
 * Fetch remaining observation template pages after the initial batch.
 * Runs in Phase 3 of sync, AFTER context is loaded so UI is already unblocked.
 * Progress callback enables real-time percentage updates during long syncs.
 */
export const fetchObservationTemplatesRemainingPages = async (
  timestamp: string | null,
  startPage: number,
  totalPages: number,
  onProgress?: (currentPage: number, totalPages: number) => void,
): Promise<boolean> => {
  let currentPage = startPage;
  let lastResult = true;

  while (currentPage <= totalPages) {
    const response = await apiService.get<IObservationTemplateResponse>(
      `/api/v1/sync-observation-templates/?page_size=${PAGE_SIZE}&page=${currentPage}&c=${Date.now()}&${
        timestamp ? `last_pulled_at=${timestamp}` : ''
      }`,
    );

    const data = response.data;

    try {
      hydrateWrongChoiceOptions(data);
      await extractSyncResponse(OBSERVATION_TEMPLATE_MODELS, data, coachingDB);
    } catch (e) {
      lastResult = false;
      console.error(
        `Unable to add data for ${OBSERVATION_TEMPLATE_MODELS} (page ${currentPage})`,
        e,
      );
    }

    // Report progress after each page
    if (onProgress) {
      onProgress(currentPage, totalPages);
    }

    currentPage++;
  }

  return lastResult;
};

export const fetchObservationContext = async (
  timestamp: string | null,
  pageNumber = 1,
): Promise<boolean> => {
  const response = await apiService.get<IObservationContextResponse>(
    `/api/v1/sync-observation-context/?page_size=${PAGE_SIZE}&page=${pageNumber}&c=${Date.now()}&${
      timestamp ? `last_pulled_at=${timestamp}` : ''
    }`,
  );

  const data = response.data;
  let lastResult = true;
  const models = [
    'regions',
    'schools',
    'teachers',
    'schoolClasses',
    'schoolClassSubjects',
    'lessonPlansMini',
    'bookChapters',
    'grades',
    'subjects',
    'gradeSubjects',
  ] as const;

  try {
    await extractSyncResponse(models, data, coachingDB);
  } catch (e) {
    lastResult = false;
    console.error(`Unable to add data for ${models}`, e);
  }

  const modelCounts = getModelCount(models, data);
  const maxPages = Math.max(...modelCounts, 0) / PAGE_SIZE;

  if (pageNumber <= maxPages) {
    lastResult =
      lastResult && (await fetchObservationContext(timestamp, pageNumber + 1));
  }

  return lastResult;
};

export const fetchVisits = async (
  timestamp: string | null,
  pageNumber = 1,
): Promise<boolean> => {
  const response = await apiService.get<IVisitResponse>(
    `/api/v1/sync-visits/?page_size=${PAGE_SIZE}&page=${pageNumber}&c=${Date.now()}&${
      timestamp ? `last_pulled_at=${timestamp}` : ''
    }`,
  );

  const data = response.data;
  let lastResult = true;
  const models = [
    'visitPlans',
    'schoolVisits',
    'teacherVisits',
    'coachesObservations',
  ] as const;

  try {
    await extractSyncResponse(models, data, coachingDB);
  } catch (e) {
    lastResult = false;
    console.error(`Unable to add data for ${models}`, e);
  }

  const modelCounts = getModelCount(models, data);
  const maxPages = Math.max(...modelCounts, 0) / PAGE_SIZE;

  if (pageNumber <= maxPages) {
    lastResult = lastResult && (await fetchVisits(timestamp, pageNumber + 1));
  }

  return lastResult;
};

interface NotFoundError extends Error {
  status: number;
}

interface ApiErrorResponse {
  response?: {
    status?: number;
  };
  status?: number;
}

export const generateObservationFeedback = async (
  input: IGenerateFeedbackRequest,
  retrying = false,
): Promise<IGenerateFeedbackResponse> => {
  try {
    // Both union members already have the correct structure for the API
    const requestBody = input; // axios-case-converter will convert to snake_case

    const response = await apiService.post<IGenerateFeedbackResponse>(
      '/api/v1/generate-observations-feedback/',
      requestBody,
    );

    return response.data;
  } catch (error: unknown) {
    const apiError = error as ApiErrorResponse;
    // If we get 404 with observationId mode and not retrying, throw special error
    if (
      !retrying &&
      'observationId' in input &&
      (apiError.response?.status === 404 || apiError.status === 404)
    ) {
      const notFoundError = new Error(
        'Observation not found in backend',
      ) as NotFoundError;
      notFoundError.status = 404;
      throw notFoundError;
    }

    console.error('feedback API error', error);
    throw error;
  }
};

export const fetchObservationFeedbacks = async (
  timestamp: string | null,
  pageNumber = 1,
): Promise<boolean> => {
  const response = await apiService.get<IObservationFeedbackResponse>(
    `/api/v1/sync-observation-feedbacks/?page_size=${PAGE_SIZE}&page=${pageNumber}&c=${Date.now()}&${
      timestamp ? `last_pulled_at=${timestamp}` : ''
    }`,
  );

  const data = response.data;
  let lastResult = true;
  const models = ['observationFeedbacks'] as const;

  try {
    // Audit-Proofing: Backup local drafts before the sync overwrites the table.
    // Only materialize rows with actual drafts. draftTeacherResponse is not
    // indexed (see models/coaching.ts), so we can't avoid the row iteration,
    // but Dexie's .filter() streams rows through the predicate and allocates
    // only passing rows into the result array — preventing OOM crashes on
    // low-end Android devices when observationFeedbacks grows to 10k+ rows.
    const existingDrafts = await coachingDB.observationFeedbacks
      .filter(
        (r) => r.draftTeacherResponse != null && r.draftTeacherResponse !== '',
      )
      .toArray();
    const draftsMap = new Map(
      existingDrafts.map((r) => [r.id, r.draftTeacherResponse]),
    );

    // Server data is synced by definition. Stamp syncStatus before bulkPut
    // so the updateHook doesn't fall through and incorrectly stamp 'updated'.
    const feedbackPayload = data.observationFeedbacks;
    if (feedbackPayload) {
      for (const key of ['created', 'updated'] as const) {
        if (feedbackPayload[key]?.data) {
          feedbackPayload[key].data = feedbackPayload[key].data.map(
            (record: any) => ({
              ...record,
              syncStatus: SyncRecordStatus.Synced,
            }),
          );
        }
      }
    }

    await extractSyncResponse(models, data, coachingDB);

    // Restore drafts after sync overwrite
    if (draftsMap.size > 0) {
      await Promise.all(
        Array.from(draftsMap.entries()).map(([id, draft]) =>
          coachingDB.observationFeedbacks.update(id, {
            draftTeacherResponse: draft,
            syncStatus: SyncRecordStatus.Synced, // Draft is local-only — don't trigger re-sync
          }),
        ),
      );
    }
  } catch (e) {
    lastResult = false;
    console.error(`Unable to add data for ${models}`, e);
  }

  const modelCounts = getModelCount(models, data);
  const maxPages = Math.max(...modelCounts) / PAGE_SIZE;

  if (pageNumber <= maxPages) {
    lastResult =
      lastResult &&
      (await fetchObservationFeedbacks(timestamp, pageNumber + 1));
  }

  return lastResult;
};

export const uploadObservationAudio = async (
  observation: IObservation,
  audioBlob: Blob,
  fileName: string,
  onUploadProgress?: (progressEvent: any) => void,
) => {
  // Defensive Feature Flag & User Profile Gate for background sync safety
  try {
    const isTeacher = (observation.userProfileType || '').toLowerCase().includes('teacher');
    const flagEnabled = (await userDB.getFeatureFlag(FeatureFlagNames.teachingReviewEnabled)) ?? false;
    if (!flagEnabled || !isTeacher) {
      tlLogger.log('CoachingAPI:UploadAudio:SkippedDefensiveGate', {
        observationId: observation.id,
        flagEnabled,
        profileType: observation.userProfileType,
      });
      // Skip background/manual upload cleanly
      return { data: { status: 'skipped' } };
    }
  } catch (flagErr) {
    tlLogger.error('CoachingAPI:UploadAudio:DefensiveGateError', {
      observationId: observation.id,
      error: flagErr,
    });
  }

  // Get correct schoolClassSubject - observation might have timetable ID instead
  let schoolClassSubject: number | null = null;

  try {
    const { lessonPlanDB } = await import('..');

    // First, check if observation.schoolClassSubject is actually a timetable ID
    if (observation.schoolClassSubject) {
      const timetable = await lessonPlanDB.schoolClassTimetables.get(
        observation.schoolClassSubject,
      );

      if (timetable && timetable.schoolClassSubject) {
        // observation.schoolClassSubject was a timetable ID, use its schoolClassSubject field
        schoolClassSubject = timetable.schoolClassSubject;
        tlLogger.log(
          'CoachingAPI:UploadAudio:SchoolClassSubjectFromTimetableId',
          {
            observationId: observation.id,
            timetableId: observation.schoolClassSubject,
            schoolClassSubject,
          },
        );
      } else {
        // observation.schoolClassSubject is the actual value
        schoolClassSubject = observation.schoolClassSubject;
        tlLogger.log('CoachingAPI:UploadAudio:SchoolClassSubjectDirect', {
          observationId: observation.id,
          schoolClassSubject,
        });
      }
    } else if (observation.userProfileId) {
      // No schoolClassSubject, try to get from profile's timetable
      const timetables = await lessonPlanDB.schoolClassTimetables
        .where('profile')
        .equals(observation.userProfileId)
        .toArray();

      if (
        timetables &&
        timetables.length > 0 &&
        timetables[0].schoolClassSubject
      ) {
        schoolClassSubject = timetables[0].schoolClassSubject;
        tlLogger.log('CoachingAPI:UploadAudio:SchoolClassSubjectFromProfile', {
          observationId: observation.id,
          schoolClassSubject,
          timetableId: timetables[0].id,
        });
      }
    }
  } catch (error) {
    tlLogger.log('CoachingAPI:UploadAudio:TimetableLookupFailed', {
      observationId: observation.id,
      error,
    });
    // Fallback to observation value if timetable lookup fails
    schoolClassSubject = observation.schoolClassSubject;
  }

  const token = userCache.getTokenFromCache();
  const baseUrl =
    (await userCache.getBaseUrl()) ?? process.env.NX_PUBLIC_BASE_URI;

  tlLogger.log('CoachingAPI:UploadAudio:Start', {
    observationId: observation.id,
    fileName,
    size: audioBlob.size,
  });

  const createFormData = () => {
    const formData = new FormData();
    formData.append('audio', audioBlob, fileName);
    formData.append('id', observation.id);
    // Explicitly using camelCase names as backend requires them and apiService's
    // snake_case middleware must be bypassed for this specific multipart upload.
    formData.append('userProfileId', String(observation.userProfileId));
    formData.append('userProfileType', observation.userProfileType || '');
    formData.append('template', String(observation.template || 1));
    formData.append('start_time', observation.startTime);
    // Reason: If audio is being uploaded for AI processing, the recording is terminal.
    // We force 'completed' here to prevent sync race conditions from creating 'in_progress' drafts on BE.
    formData.append('status', 'completed');
    formData.append('observation_date', observation.observationDate);
    formData.append('number_of_boys', String(observation.numberOfBoys));
    formData.append('number_of_girls', String(observation.numberOfGirls));

    if (observation.lessonPlan) {
      formData.append('lesson_plan', String(observation.lessonPlan));
    }
    if (observation.coreLessonPlan) {
      formData.append('core_lesson_plan', String(observation.coreLessonPlan));
    }
    formData.append('school_class_subject', String(schoolClassSubject || ''));
    if (observation.visit) {
      formData.append('visit', observation.visit);
    }

    if (observation.imagesS3Urls && observation.imagesS3Urls.length > 0) {
      observation.imagesS3Urls.forEach((url) => {
        formData.append('images_s3_urls', url);
      });
    }

    tlLogger.verbose('CoachingAPI:UploadAudio:FormData', {
      observationId: observation.id,
      userProfileId: observation.userProfileId,
      userProfileType: observation.userProfileType,
      schoolClassSubject: schoolClassSubject,
      lessonPlan: observation.lessonPlan,
      coreLessonPlan: observation.coreLessonPlan,
      template: observation.template,
      startTime: observation.startTime,
      observationDate: observation.observationDate,
      numberOfBoys: observation.numberOfBoys,
      numberOfGirls: observation.numberOfGirls,
      visit: observation.visit,
      audioFileName: fileName,
      audioSize: audioBlob.size,
      imagesS3UrlsCount: observation.imagesS3Urls?.length || 0,
    });

    return formData;
  };

  // Reason: Raw XMLHttpRequest with FormData + Blob reference streams directly
  // from blob store without buffering (unlike axios's XHR adapter which calls
  // under-the-hood ArrayBuffer transformations and causes memory spikes on large files).
  const executePost = (accessToken: string | undefined) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Set up progress tracking
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded * 100) / event.total);
          tlLogger.log('CoachingAPI:UploadAudio:Progress', {
            loaded: event.loaded,
            total: event.total,
            progress,
          });
          onUploadProgress?.({
            loaded: event.loaded,
            total: event.total,
          });
        }
      });

      // Set up completion handlers
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve({ data });
          } catch {
            resolve({ data: {} });
          }
        } else {
          const errorData = (() => {
            try {
              return JSON.parse(xhr.responseText);
            } catch {
              return {};
            }
          })();
          const error: any = new Error(
            `Upload failed: ${xhr.status} ${xhr.statusText}`,
          );
          error.response = { status: xhr.status, data: errorData };
          reject(error);
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload aborted'));
      });

      // Set up request
      xhr.open(
        'POST',
        `${baseUrl}/api/v1/internal/digital_coach/process-audio-observation/`,
      );
      if (accessToken) {
        xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
      }

      // Send FormData (NOT buffered — streams directly)
      xhr.send(createFormData());
    });
  };

  try {
    // Final network check before making API call
    if (!navigator.onLine) {
      throw new Error('No network connection');
    }

    const result = await executePost(token?.access);
    tlLogger.log('CoachingAPI:UploadAudio:Success', {
      observationId: observation.id,
    });
    return result;
  } catch (error: any) {
    // If token is expired (401), try refreshing it once manually
    if (error.response?.status === 401) {
      tlLogger.log('CoachingAPI:UploadAudio:TokenExpired', {
        observationId: observation.id,
      });
      try {
        const refreshedToken = await updateToken();
        if (refreshedToken?.access) {
          return await executePost(refreshedToken.access);
        }
      } catch (refreshError) {
        tlLogger.error('CoachingAPI:UploadAudio:RefreshFailed', {
          error: refreshError,
        });
      }
    }

    tlLogger.error('CoachingAPI:UploadAudio:Failed', {
      observationId: observation.id,
      error: error.response?.data || error.message,
    });
    throw error;
  }
};

export interface IPresignedUrlResponse {
  urls: Array<{
    uploadUrl: string;
    finalUrl: string;
    fileKey: string;
  }>;
}

/**
 * Fetches presigned S3 upload URLs from the backend.
 */
export const getPresignedUploadUrls = async (
  observationId: string,
  fileCount: number,
): Promise<IPresignedUrlResponse> => {
  const response = await apiService.post<IPresignedUrlResponse>(
    '/api/v1/internal/digital_coach/get-presigned-urls/',
    {
      observationId,
      fileCount,
    },
  );
  return response.data;
};

/**
 * Uploads a file directly to S3 using a presigned URL.
 * Uses native fetch to avoid memory overhead on large files.
 */
export const uploadFileToS3 = async (
  uploadUrl: string,
  file: Blob | File,
  contentType: string,
) => {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType,
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
  }

  return response;
};

/**
 * Uploads a file directly to S3 using a presigned URL with progress tracking.
 */
export const uploadFileToS3WithProgress = async (
  uploadUrl: string,
  file: Blob | File,
  contentType: string,
  onProgress?: (progress: { loaded: number; total: number }) => void,
) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        onProgress?.({
          loaded: event.loaded,
          total: event.total,
        });
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.response);
      } else {
        reject(new Error(`S3 upload failed: ${xhr.status} ${xhr.statusText}`));
      }
    });

    xhr.addEventListener('error', () => reject(new Error('S3 Network error')));
    xhr.addEventListener('abort', () => reject(new Error('S3 Upload aborted')));

    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.send(file);
  });
};
